import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bucket, GetSalesInsightsAggregatedRequest, SalesInsightsAggregateItem, SalesInsightsCompareAggregateResponse, useSalesInsightsCompareAggregated } from '../../../../api/indicators';
import { dateRange, formatDateParam } from '../../../../helpers/dateUtils';
import { DashboardFiltersContext } from '../filters/DashboardFiltersContext';
import { DashboardContext } from '../../DashboardContext';
import AreaChartCompared from '../charts/areaChart/AreaChartCompared';
import { AreaChartDataItem } from '../charts/areaChart/AreaChartBase';
import { SettingsContext } from '../../../../context/SettingsContext';
import { getBucketFromComparedRanges } from '../../dashboardHelpers';

const grossMargin = (salesInsightsAggregateItem: SalesInsightsAggregateItem) => (
    (salesInsightsAggregateItem?.salesInsights.totalSalesExcTax ?? 0)
    - (salesInsightsAggregateItem?.salesInsights.costPriceExcTax ?? 0)
);

const ComparedNetMarginExcTax = () => {
    const { t } = useTranslation();
    const { calendarPreferences } = useContext(SettingsContext);
    const { filters, dateRangeConfig } = useContext(DashboardFiltersContext);
    const { companyConfig, refreshDate } = useContext(DashboardContext);
    const [lineBarChartData, setLineBarChartData] = useState<AreaChartDataItem[]>([]);
    const [lineBarChartDataCompared, setLineBarChartDataCompared] = useState<AreaChartDataItem[]>([]);
    const grossMarginLabel = t("screens.dashboarding.charts.netMargin");

    const initialStartDate = filters.periodFrom
        ? filters.periodFrom
        : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate;

    const initialEndDate = filters.periodTo
        ? filters.periodTo
        : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate;

    const initialCompareStart = filters.comparePeriodFrom
        ? filters.comparePeriodFrom
        : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate;

    const initialCompareEnd = filters.comparePeriodTo
        ? filters.comparePeriodTo
        : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate;

    const [params, setParams] = useState<GetSalesInsightsAggregatedRequest>({
        fields: 'netMargin',
        startDate: formatDateParam(initialStartDate),
        endDate: formatDateParam(initialEndDate),
        compareStartDate: formatDateParam(initialCompareStart),
        compareEndDate: formatDateParam(initialCompareEnd),
        locations: filters?.locations?.join(',') ? filters?.locations?.join(',') : companyConfig?.locations?.map(location => location.id).join(','),
        locationArea: filters?.locationArea?.id,
        businessStartHour: calendarPreferences.daysStart,
        bucket: getBucketFromComparedRanges(
            initialStartDate,
            initialEndDate,
            initialCompareStart,
            initialCompareEnd
        )
    });

    const {
        data: salesInsightsAggregate,
        isLoading,
        isError,
        refetch: fetchTotalGrossMargin
    } = useSalesInsightsCompareAggregated<SalesInsightsCompareAggregateResponse>(params, 'sales-insights-aggregate-compare-net-margin');

    useEffect(() => {
    if (salesInsightsAggregate?.salesInsightsAggregate) {
        setLineBarChartData(
            salesInsightsAggregate.salesInsightsAggregate.map(
                (salesInsightsAggregateItem) => ({
                    dateTime: new Date(salesInsightsAggregateItem.dateTime),
                    [grossMarginLabel]: salesInsightsAggregateItem.salesInsights.netMargin ?? 0
                })
            )
        );
    }

    if (salesInsightsAggregate?.comparedSalesInsightsAggregate) {
        setLineBarChartDataCompared(
            salesInsightsAggregate.comparedSalesInsightsAggregate.map(
                (salesInsightsAggregateItem) => ({
                    dateTime: new Date(salesInsightsAggregateItem.dateTime),
                    [grossMarginLabel]: salesInsightsAggregateItem.salesInsights.netMargin ?? 0
                })
            )
        );
    }
}, [salesInsightsAggregate]);

    useEffect(() => {
        const start = filters.periodFrom
            ? filters.periodFrom
            : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate;

        const end = filters.periodTo
            ? filters.periodTo
            : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate;

        const compareStart = filters.comparePeriodFrom
            ? filters.comparePeriodFrom
            : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate;

        const compareEnd = filters.comparePeriodTo
            ? filters.comparePeriodTo
            : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate;

        const bucket = getBucketFromComparedRanges(
            start,
            end,
            compareStart,
            compareEnd
        );
        setParams({
            ...params,
            startDate: formatDateParam(start),
            endDate: formatDateParam(end),
            compareStartDate: formatDateParam(compareStart),
            compareEndDate: formatDateParam(compareEnd),
            locations: filters?.locations?.join(','),
            locationArea: filters?.locationArea?.id,
            bucket

        });
    }, [filters]);

    useEffect(() => { fetchTotalGrossMargin(); }, [params]);

    useEffect(() => {
        if (refreshDate != null) {
            fetchTotalGrossMargin();
        }
    }, [refreshDate]);

    return (
        <AreaChartCompared
            title={t('screens.dashboarding.charts.netMargin')}
            currency={salesInsightsAggregate?.currency}
            description={t('screens.dashboarding.charts.excludingTax')}
            isError={isError}
            isLoading={isLoading}
            leftAxisLabel={t("screens.dashboarding.charts.amount")}
            data={lineBarChartData}
            bucket={params.bucket as Bucket}
            setBucket={(value: string) => setParams({
                ...params,
                bucket: value as Bucket
            })}
            height={560}
            dataCompared={lineBarChartDataCompared}
            currentDateFrom={filters.periodFrom ? filters.periodFrom : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate}
            currentDateTo={filters.periodTo ? filters.periodTo : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate}
            comparedDateFrom={filters.comparePeriodFrom ? filters.comparePeriodFrom : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate}
            comparedDateTo={filters.comparePeriodTo ? filters.comparePeriodTo : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate}
        />
    )
}

export default ComparedNetMarginExcTax;

